/**
 * UPT-2079 L14a (web §4, dépôt de démonstration) — ressources image du dossier EN MÉMOIRE (jamais en `localStorage` : ce sont des octets). Le dépôt imite le serveur
 * (rest §4.8) : signature PNG ou JPEG (SVG, WebP et GIF refusés), dimensions lues dans l'en-tête avant tout (côté long ≤ 1 920), ≤ 512 Kio, 32 par dossier,
 * dédoublonnage par empreinte, attestation de droits obligatoire, suppression refusée si une version la cite. La démonstration ne ré-encode pas : elle garde les
 * octets reçus (déjà ré-encodés par le navigateur).
 */
import { MODULE_ASSET_LIMITS, type IsoDateTime, type ModuleAsset, type ModuleAssetOrigin } from './contracts';
import { sniffImage } from '../presentation/assetThumb';
import { sanitizeSvg } from '../prototypes/sourceResources';

const K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74,
  0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d,
  0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e,
  0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
  0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

/** SHA-256 en TypeScript pur (la démonstration ne dépend pas de `crypto.subtle`, absent de certains environnements de test). */
export const sha256Hex = (bytes: Uint8Array): string => {
  const h = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
  const length = bytes.length;
  const padded = new Uint8Array(((length + 9 + 63) >> 6) << 6);
  padded.set(bytes);
  padded[length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor((length * 8) / 0x100000000));
  view.setUint32(padded.length - 4, (length * 8) >>> 0);
  const w = new Uint32Array(64);
  const rotr = (value: number, bits: number) => (value >>> bits) | (value << (32 - bits));
  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let i = 0; i < 16; i += 1) w[i] = view.getUint32(offset + i * 4);
    for (let i = 16; i < 64; i += 1) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, hh] = h;
    for (let i = 0; i < 64; i += 1) {
      const t1 = (hh + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K[i] + w[i]) >>> 0;
      const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    h[0] = (h[0] + a) >>> 0;
    h[1] = (h[1] + b) >>> 0;
    h[2] = (h[2] + c) >>> 0;
    h[3] = (h[3] + d) >>> 0;
    h[4] = (h[4] + e) >>> 0;
    h[5] = (h[5] + f) >>> 0;
    h[6] = (h[6] + g) >>> 0;
    h[7] = (h[7] + hh) >>> 0;
  }
  return h.map((value) => value.toString(16).padStart(8, '0')).join('');
};

const httpError = (status: number, code: string): Error => Object.assign(new Error(code), { response: { status, data: { code } } });

export interface DemoAssetInput {
  origin: ModuleAssetOrigin;
  label: string | null;
  rightsAttested: boolean;
  attestedBy: string;
  now: IsoDateTime;
}

type Stored = { meta: ModuleAsset; bytes: Uint8Array };

export class DemoAssetStore {
  private readonly byDossier = new Map<string, Map<string, Stored>>();

  clear(): void {
    this.byDossier.clear();
  }

  private of(dossier: string): Map<string, Stored> {
    let map = this.byDossier.get(dossier);
    if (!map) {
      map = new Map();
      this.byDossier.set(dossier, map);
    }
    return map;
  }

  /** Triées par date puis empreinte (rest §4.8) ; `usedBy` est posé par l'appelant. */
  list(dossier: string): ModuleAsset[] {
    return [...this.of(dossier).values()]
      .map((entry) => ({ ...entry.meta, origin: { ...entry.meta.origin }, usedBy: [...entry.meta.usedBy] }))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.sha256.localeCompare(b.sha256));
  }

  bytes(dossier: string, sha256: string): Uint8Array | null {
    return this.of(dossier).get(sha256)?.bytes.slice() ?? null;
  }

  /** Contrôle et ajout (rest §4.8) ; même empreinte déjà présente → l'existante, attestation inchangée. */
  add(dossier: string, input: Uint8Array, details: DemoAssetInput): ModuleAsset {
    if (!details.rightsAttested) throw httpError(400, 'asset.rights_required');
    if (input.length > 1_048_576) throw httpError(413, 'asset.too_large');
    let sniffed: { mediaType: ModuleAsset['mediaType']; width: number; height: number } | null = sniffImage(input);
    if (!sniffed && input[0] === 60) {
      const text = decodeURIComponent(Array.from(input, byte => `%${byte.toString(16).padStart(2, '0')}`).join(''));
      if (/<!DOCTYPE|<!ENTITY/i.test(text)) throw httpError(400, 'asset.type');
      const svg = new DOMParser().parseFromString(text, 'image/svg+xml').documentElement;
      if (svg.localName !== 'svg' || (svg.hasAttribute('xmlns') && svg.getAttribute('xmlns') !== 'http://www.w3.org/2000/svg')) throw httpError(400, 'asset.type');
      const clean = sanitizeSvg(svg);
      const box = (svg.getAttribute('viewBox') ?? '').trim().split(/[\s,]+/);
      const width = Math.ceil(Number.parseFloat(svg.getAttribute('width') ?? box[2] ?? '24'));
      const height = Math.ceil(Number.parseFloat(svg.getAttribute('height') ?? box[3] ?? '24'));
      input = Uint8Array.from(encodeURIComponent(clean).replace(/%([0-9A-F]{2})/g, (_, hex: string) => String.fromCharCode(Number.parseInt(hex, 16))), char => char.charCodeAt(0));
      sniffed = { mediaType: 'image/svg+xml', width, height };
    }
    if (!sniffed && input.length >= 48) {
      const magic = String.fromCharCode(...input.slice(0, 4));
      if (magic === 'wOFF' || magic === 'wOF2') {
        const view = new DataView(input.buffer, input.byteOffset, input.byteLength);
        if (view.getUint32(8) !== input.length || view.getUint16(12) < 1 || view.getUint16(12) > 256 || view.getUint16(14) !== 0 || view.getUint32(16) > 8_388_608) throw httpError(400, 'asset.type');
        if (magic === 'wOF2' && (view.getUint32(20) < 1 || view.getUint32(20) > input.length - 48)) throw httpError(400, 'asset.type');
        sniffed = { mediaType: magic === 'wOFF' ? 'font/woff' : 'font/woff2', width: 1, height: 1 };
      }
    }
    if (!sniffed) throw httpError(400, 'asset.type');
    if (
      sniffed.width < 1 ||
      sniffed.height < 1 ||
      Math.max(sniffed.width, sniffed.height) > MODULE_ASSET_LIMITS.maxSide
    ) {
      throw httpError(400, 'asset.dimensions');
    }
    if (input.length > MODULE_ASSET_LIMITS.maxBytes) throw httpError(413, 'asset.too_large');
    const sha256 = sha256Hex(input);
    const store = this.of(dossier);
    const existing = store.get(sha256);
    if (existing) return { ...existing.meta, origin: { ...existing.meta.origin }, usedBy: [...existing.meta.usedBy] };
    if (store.size >= MODULE_ASSET_LIMITS.perDossier) throw httpError(409, 'asset.limit');
    const meta: ModuleAsset = {
      sha256,
      mediaType: sniffed.mediaType,
      width: sniffed.width,
      height: sniffed.height,
      bytes: input.length,
      origin: { ...details.origin },
      label: details.label,
      rightsAttestedBy: details.attestedBy,
      rightsAttestedAt: details.now,
      createdAt: details.now,
      usedBy: [],
    };
    store.set(sha256, { meta, bytes: input.slice() });
    return { ...meta, origin: { ...meta.origin }, usedBy: [] };
  }

  /** 409 `asset.in_use` si `usedBy` n'est pas vide ; la suppression d'une ressource absente est sans effet. */
  remove(dossier: string, sha256: string, usedBy: readonly number[]): void {
    if (usedBy.length > 0) throw httpError(409, 'asset.in_use');
    this.of(dossier).delete(sha256);
  }

  deleteDossier(dossier: string): void {
    this.byDossier.delete(dossier);
  }
}

/** Octets d'un Blob (lecteur de fichier : `Blob.arrayBuffer()` n'existe pas dans tous les environnements de test). */
export const blobToBytes = (blob: Blob): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsArrayBuffer(blob);
  });
